import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import React, { useMemo } from 'react';
import type { BriefingData } from '../../lib/api';
import { computePermitPathway, findFeaturedPipeline } from '../../lib/parcel-analysis';
import { buildUnifiedDDChecklist, CATEGORY_ABBREV, type DDCategory, type DDStatus } from '../briefing/BriefingContent';

const shortId = (id: string) => id.replace(/-/g, '').slice(0, 8).toUpperCase();
const todayStr = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

const STATUS_LABEL: Record<DDStatus, string> = {
  ADDRESSED: 'Addressed',
  FLAGGED: 'Flagged',
  'REQUIRES INQUIRY': 'Inquiry',
};

const STATUS_CLASS: Record<DDStatus, string> = {
  ADDRESSED: 'pass',
  FLAGGED: 'caution',
  'REQUIRES INQUIRY': 'inquiry',
};

function cellStatus(cat: DDCategory): 'pass' | 'caution' | 'inquiry' {
  const allAddressed = cat.items.every(d => d.status === 'ADDRESSED');
  if (allAddressed) return 'pass';
  if (cat.items.some(d => d.status === 'REQUIRES INQUIRY')) return 'inquiry';
  return 'caution';
}

export const DDChecklistV2: React.FC<{ data: BriefingData }> = ({ data }) => {
  const p = data.parcel;
  const featured = useMemo(() => findFeaturedPipeline(data.parcel, data.pipelines, data.carveOutAggregate?.slug), [data.parcel, data.pipelines, data.carveOutAggregate?.slug]);
  const featuredCapacity = featured ? pipelineCapacityDisplay(featured).capacityMmcf ?? undefined : undefined;
  const isA1 = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
  const permitPathway = useMemo(() => computePermitPathway(p, featuredCapacity, isA1 ? { crownpineA1: true } : undefined), [p, featuredCapacity, isA1]);
  const categories = useMemo(() => buildUnifiedDDChecklist(data, p, permitPathway, true), [data, p, permitPathway]);

  const allItems = categories.flatMap(c => c.items);
  const addressedCount = allItems.filter(d => d.status === 'ADDRESSED').length;
  const flaggedCount = allItems.filter(d => d.status === 'FLAGGED').length;
  const inquiryCount = allItems.filter(d => d.status === 'REQUIRES INQUIRY').length;

  return (
    <div className="v2-sheet">
      {/* Dateline */}
      <header className="v2-dateline">
        <div className="v2-seq">
          {(p.parcel_id_local || 'UNNAMED-PARCEL').toUpperCase()} &nbsp;&middot;&nbsp;
          <strong>{shortId(p.id)}</strong> &nbsp;&middot;&nbsp;
          {p.county.toUpperCase()} &middot; {p.state} &nbsp;&middot;&nbsp; DD CHECKLIST
        </div>
        <div className="v2-right">
          {todayStr} &nbsp;&middot;&nbsp; <strong>CONFIDENTIAL</strong>
        </div>
      </header>

      {/* Header */}
      <div className="v2-dd-header">
        <h1 className="v2-dd-title">Due Diligence</h1>
        <div className="v2-dd-summary">
          <span className="v2-dd-summary-inquiry">{inquiryCount} require inquiry</span>
          {' · '}
          <span className="v2-dd-summary-flagged">{flaggedCount} flagged</span>
          {' · '}
          <span className="v2-dd-summary-addressed">{addressedCount} addressed</span>
        </div>
      </div>

      {/* Heat strip */}
      <div className="v2-dd-strip">
        {categories.map(cat => {
          const catAddressed = cat.items.filter(d => d.status === 'ADDRESSED').length;
          const status = cellStatus(cat);
          return (
            <div key={cat.name} className={`v2-dd-strip-cell status-${status}`}>
              <div className="v2-dd-abbrev">{CATEGORY_ABBREV[cat.name] || cat.name.slice(0, 4).toUpperCase()}</div>
              <div className={`v2-dd-count status-${status}`}>{catAddressed}/{cat.items.length}</div>
            </div>
          );
        })}
      </div>

      {/* Category sections */}
      <div className="v2-dd-body">
        {categories.map((cat, ci) => {
          const catAddressed = cat.items.filter(d => d.status === 'ADDRESSED').length;
          return (
            <section key={cat.name} className="v2-dd-category">
              <div className="v2-section-head">
                <span className="v2-num-tag">{String(ci + 1).padStart(2, '0')}</span>
                <h2>{cat.name}</h2>
                <span className="v2-meta">{catAddressed} / {cat.items.length} addressed</span>
              </div>
              <div className="v2-dd-rows">
                {cat.items.map((item, ii) => (
                  <div key={ii} className="v2-dd-row">
                    <div className="v2-dd-item-name">{item.item}</div>
                    <span className={`v2-badge ${STATUS_CLASS[item.status]}`}>{STATUS_LABEL[item.status]}</span>
                    <div className="v2-dd-item-summary">{item.summary}</div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="v2-footer">
        <div className="v2-brand">
          Atwater <em>Energy</em>
          <div className="v2-mono">Due diligence screening &middot; automated assessment &middot; {todayStr}</div>
        </div>
        <div className="v2-sources">
          Status reflects automated screening only. All items require field verification before commitment.
        </div>
      </footer>
    </div>
  );
};
